import {
  Component,
  ChangeDetectionStrategy,
  input,
  signal,
  computed,
  inject,
  viewChild,
  effect,
} from '@angular/core';
import { ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, tap } from 'rxjs/operators';
import { IImageCarouselItem } from './image-carousel.model';
import { SwipeDirective } from '../../directives/swipe.directive';

@Component({
  selector: 'shrl-image-carousel',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    RouterLink,
    TranslateModule,
    SwipeDirective,
  ],
  templateUrl: './image-carousel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageCarouselComponent {
  readonly items = input.required<IImageCarouselItem[]>();
  readonly #translate = inject(TranslateService);
  readonly #breakpointObserver = inject(BreakpointObserver);

  readonly currentPage = signal(0);
  readonly carouselContainer =
    viewChild<ElementRef<HTMLDivElement>>('carouselContainer');

  readonly itemsPerPage = toSignal(
    this.#breakpointObserver
      .observe(['(min-width: 1024px)', '(min-width: 640px)'])
      .pipe(
        map((result) => {
          if (result.breakpoints['(min-width: 1024px)']) {
            return 4;
          }
          if (result.breakpoints['(min-width: 640px)']) {
            return 2;
          }
          return 1;
        }),
        tap(() => {
          this.currentPage.set(0);
        })
      ),
    { initialValue: 1 }
  );

  readonly isExternalUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  readonly getTranslatedValue = (
    value: string | { translationKey: string } | undefined
  ): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return this.#translate.instant(value.translationKey);
  };

  readonly totalPages = computed(() =>
    Math.ceil(this.items().length / this.itemsPerPage())
  );

  readonly hasNext = computed(() => this.items().length > this.itemsPerPage());

  readonly hasPrevious = computed(
    () => this.items().length > this.itemsPerPage()
  );

  readonly transformOffset = signal(0);

  constructor() {
    this.updateTransformOffset();
  }

  private updateTransformOffset = () => {
    effect(() => {
      const page = this.currentPage();
      const itemsPerPage = this.itemsPerPage();
      const container = this.carouselContainer()?.nativeElement;
      if (!container) {
        this.transformOffset.set(0);
        return;
      }
      const containerWidth = container.offsetWidth;
      const gap = 16; // gap-4 = 1rem = 16px
      const itemWidth =
        (containerWidth - (itemsPerPage - 2) * gap) / itemsPerPage;
      const pageWidth = itemsPerPage * itemWidth + (itemsPerPage - 1) * gap;
      const offset = page * pageWidth;
      this.transformOffset.set(offset);
    });
  };

  next = () => {
    const page = this.currentPage();
    const total = this.totalPages();
    if (page < total - 1) {
      this.currentPage.set(page + 1);
    } else {
      this.currentPage.set(0);
    }
  };

  previous = () => {
    const page = this.currentPage();
    const total = this.totalPages();
    if (page > 0) {
      this.currentPage.set(page - 1);
    } else {
      this.currentPage.set(Math.max(0, total - 1));
    }
  };

  goToPage = (pageIndex: number) => {
    const total = this.totalPages();
    if (pageIndex >= 0 && pageIndex < total) {
      this.currentPage.set(pageIndex);
    }
  };

  readonly dotsCount = computed(() => {
    const total = this.totalPages();
    return total > 1 ? Array.from({ length: total }, (_, i) => i) : [];
  });

  isDotActive = (dotIndex: number): boolean => dotIndex === this.currentPage();

  readonly onSwipeLeft = () => this.next();
  readonly onSwipeRight = () => this.previous();
}
